import React, { useEffect, useState } from "react";
import { Bounce, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useAuth } from "../../../../context/AuthContext";
import classes from "./AddInventoryManagement.module.css";
import CommonFooter from "../../../CommonFooter/CommonFooter";
import { env } from "../../../../constants/env";
import QuestionSection from "../../../ui/QuestionSection/QuestionSection";
import { useCertification } from "../../../../hooks/useCertification";

const questionSets = [
  {
    render: (onAnswerChange, answers, editable) => (
      <>
        <QuestionSection
          number="7.1"
          title="Procedure and/or Process for Externally Provided Products and Services"
          questions={[
            {
              content:
                "Has the laboratory defined a procedure and/or process that addresses, but is not limited to, the following?",
              hideOptions: true,
            },
            {
              content: "Selection of required products and services;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Establishment of selection criteria;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Establishment of acceptance criteria;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Selection, approval of suppliers and technical consultants;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Maintenance of approved suppliers list;",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Defining the requirements of its purchase supplies and services (purchase documentation);",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Reviewing and monitoring of the performance of its approved suppliers;",
              number: "g",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Frequency of reviewing and monitoring the performance.",
              number: "h",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.2"
          title="Procedure and/or Process for Purchasing and Inventory Control of Equipment, Reagents, and Consumables"
          questions={[
            {
              content:
                "Has the laboratory defined a procedure and/or process that addresses, but is not limited to, the following?",
              hideOptions: true,
            },
            {
              content: "Requisition, ordering and receipt of purchased items;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Establishment of acceptance and rejection criteria for purchased items;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Acceptance testing;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Storage of purchased items;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Management of inventory;",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Monitoring and handling of expired items;",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Responding to manufacturers recall or other notices",
              number: "g",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.3"
          title="Inventory and Budgeting System (Including the requirements for POCT)"
          questions={[
            {
              content: (
                <>
                  Is there a process for accurately forecasting needs for
                  services, supplies and reagents?
                  <br />
                  Note 1: External services include referral laboratories and
                  consultants
                  <br />
                  Note 2: The laboratory must have a systematic way of
                  determining its supply and testing needs through inventory
                  control and budgeting systems that take into consideration
                  past patterns, present trends, and future plans
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.4"
          title="Purchasing Specifications"
          questions={[
            {
              content: (
                <>
                  Does the laboratory provide specifications for their services,
                  supplies and consumables that are required when placing a
                  requisition?
                  <br />
                  Note: Specification could be in the form of catalogue number,
                  item number, manufacturer name, etc.
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.5"
          title="Service Supplier Performance Review"
          questions={[
            {
              content: (
                <>
                  Does laboratory management monitor the performance of external
                  suppliers (including referral laboratories, technical
                  consultants, and EQA providers) to ensure that they
                  continually meet the stated criteria of the approved
                  suppliers?
                  <br />
                  Note: All suppliers of services used by the laboratory must be
                  reviewed and monitored for their performance
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.6"
          title="Inventory Control"
          questions={[
            {
              content:
                "Does the laboratory maintain records for each reagent and consumable that contributes to the performance of examinations? These records shall include but not be limited to the following:",
              hideOptions: true,
            },
            {
              content: "Identity of the reagent or consumable;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Batch code or lot number;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Manufacturer or supplier name and contact information;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Received date, expiration date, date of entry into service and date material was taken out of service, where applicable;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Manufacturer's instruction/package insert;",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: (
                <>
                  Records of inspection of reagents and consumables when
                  received (e.g., acceptable or damaged)
                  <br />
                  <strong>
                    Note: All incoming orders must be inspected for condition
                    and completeness of the original requests, receipted, and
                    documented appropriately, date received in the Laboratory
                    and expiry date for the product should be clearly indicated
                  </strong>
                </>
              ),
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Reference to the person or persons undertaking the preparation of reagents, resuspension or combined in-house, as well as the dates of preparation and stability",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.7"
          title="Management Review of Supply Requests"
          questions={[
            {
              content: (
                <>
                  Does laboratory management review and approve the laboratory's
                  requirements for all externally provided products and
                  services?
                  <br />
                  Note: Since laboratories have different purchasing approval
                  systems, there should be a system in place that the laboratory
                  reviews final approval of their original request
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.8"
          title="Laboratory Inventory System"
          questions={[
            {
              content:
                "Note: The laboratory inventory system should reliably inform personnel of the minimum amount of stock to be kept to avoid interruptions of service due to stockouts and the maximum amount to be kept by the laboratory to prevent expiry of reagents",
              hideOptions: true,
            },
            {
              content:
                "Are inventory records complete and accurate with minimum and maximum stock levels denoted and monitored?",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Is the consumption rate of all reagents and consumables monitored?",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Are inventory/stock counts routinely performed?",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.9"
          title="Storage Areas"
          questions={[
            {
              content: (
                <>
                  Are storage areas set up and monitored appropriately?
                  <br />
                  Note: Storage of supplies and consumables must be as per the
                  manufacturer’s specifications
                </>
              ),
              hideOptions: true,
            },
            {
              content:
                "Is the storage area well-organized and free of clutter to prevent damage and deterioration?",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Are there designated places for all inventory items for easy access (separation of inspected and uninspected items)?",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Is adequate cold storage available?",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Is the humidity of the room monitored routinely, when appropriate?",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Is the temperature of the room monitored routinely?",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Is storage in direct sunlight avoided? Is direct sunlight avoided in storage areas?",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Is the storage area adequately ventilated?",
              number: "g",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Is the storage area clean and free of dust and pests?",
              number: "h",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Are storage areas access-controlled?",
              number: "i",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.10"
          title="Inventory Organization and Wastage Minimization"
          questions={[
            {
              content: (
                <>
                  Is First-Expiration-First-Out (FEFO) practiced?
                  <br />
                  Note: To minimize wastage from product expiration, inventory
                  should be organized in line with the FEFO principle. Place
                  products that will expire first in front of products with a
                  later expiration date and issue stock accordingly to ensure
                  products in use are not past their expiration date. Remember
                  that the order in which products are received is not
                  necessarily the order in which they will expire
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="7.11"
          title="Product Expiration"
          questions={[
            {
              content: (
                <>
                  Are all reagents/test kits in use (and in stock) currently
                  within the manufacturer-assigned expiration or within
                  stability?
                  <br />
                  Note 1: All reagents and test kits in use, as well as those in
                  stock, should be within the manufacturer-assigned expiry
                  dates.
                  <br />
                  Note 2: Expired controls and calibrators must not be used.
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
        <QuestionSection
          number="7.12"
          title="Disposal of Expired Products"
          questions={[
            {
              content: (
                <>
                  Are expired products labelled and disposed of properly?
                  <br />
                  Note: Expired products should be disposed of properly and
                  records maintained. If safe disposal is not available at the
                  laboratory, the manufacturer/supplier should take back the
                  expired stock at the time of their next delivery
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
      </>
    ),
  },
];

const AddInventoryManagement = () => {
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);

  const { labId, questionnaireId } = useParams(); // ✅ get questionnaireId too

  const editable = questionnaireId ? status === "Not Attached" : true;

  const {
    formData: answers,
    handleChange: handleAnswerChange,
    handleDone,
    editMode,
    loading,
    isFetching,
  } = useCertification(
    "Inventory Management", // section
    {}, // initialAnswers
    questionnaireId // questionnaireId (optional)
  );

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div
        className="d-flex align-items-center gap-2 mb-4"
        onClick={() => navigate(-1)}
        role="button"
      >
        <IoIosArrowRoundBack size={35} />
        <h2 className="m-0">
          Section 07: Supplier and Inventory Management{" "}
          {questionnaireId && "(Editing)"}
        </h2>
      </div>

      {isFetching || loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {questionSets[currentSet].render(
            handleAnswerChange,
            answers,
            editMode
          )}

          {editMode && (
            <CommonFooter
              handlePrevious={handlePrevious}
              handleCancel={() => navigate(-1)}
              handleNext={handleNext}
              handleDone={handleDone}
              questionSets={questionSets}
              currentSet={currentSet}
              cancelText="Cancel"
              doneText={
                questionnaireId ? "Update Assessment" : "Submit Assessment"
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default AddInventoryManagement;
